import { Feature, LineString, Position } from 'geojson'
import turf from 'turf'
import {
  MAX_TRAJECTORY_DISTANCE,
  MAX_TRAJECTORY_POINTS,
  MAX_TRAJECTORY_POLYGON_POINTS,
} from 'utils/constants'

/**
 * Append a map pose to the live trail. Drops points closer than
 * `MAX_TRAJECTORY_DISTANCE` and evicts the oldest when the cap is hit.
 */
export function appendTrajectoryPoint(coordinates: Position[], next: Position): Position[] {
  const lastPoint = coordinates[coordinates.length - 1]
  if (
    lastPoint &&
    turf.distance(turf.point(lastPoint), turf.point(next), 'meters') < MAX_TRAJECTORY_DISTANCE
  ) {
    return coordinates
  }
  if (coordinates.length >= MAX_TRAJECTORY_POINTS) {
    return [...coordinates.slice(-(MAX_TRAJECTORY_POINTS - 1)), next]
  }
  return [...coordinates, next]
}

/** Keep first/last and evenly sample the rest so turf.buffer stays cheap. */
export function downsampleCoordinates(coordinates: Position[], maxPoints: number): Position[] {
  if (maxPoints < 1) return []
  if (coordinates.length <= maxPoints) return coordinates
  if (maxPoints === 1) return [coordinates[coordinates.length - 1]]

  const lastIndex = coordinates.length - 1
  const sampled: Position[] = []
  let prevIndex = -1
  for (let i = 0; i < maxPoints; i++) {
    const index = i === maxPoints - 1 ? lastIndex : Math.round((i * lastIndex) / (maxPoints - 1))
    if (index === prevIndex) continue
    sampled.push(coordinates[index])
    prevIndex = index
  }
  return sampled
}

export function lineStringForTrajectoryPolygon(
  coordinates: Position[],
  maxPoints: number = MAX_TRAJECTORY_POLYGON_POINTS
): Feature<LineString> | null {
  const sampled = downsampleCoordinates(coordinates, maxPoints)
  if (sampled.length < 2) return null
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: sampled,
    },
    properties: {},
  }
}

export function capTrailPoints<T>(points: T[] | undefined | null, maxPoints: number): T[] {
  if (!points || points.length === 0) return []
  if (points.length <= maxPoints) return points
  return points.slice(-maxPoints)
}
